import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import axios from 'axios';
import { useDrag, useDrop, useDragDropManager } from 'react-dnd';
import { DndProvider, usePreview } from 'react-dnd-multi-backend';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import { useParams } from 'react-router-dom';
import { buildApiUrl } from '../utils/api';
import socket from '../socket';
import useSocketEvent from '../utils/useSocketEvent';
import { loadActiveShow, loadPlaysets } from '../integrations/quizApiAdapter';
import './css/MatrixEditor.css';

const ItemTypes = { QUESTION: 'QUESTION' };
const SAVE_DEBOUNCE_MS = 500;
const CONFLICT_POLL_MS = 30000;
const UNDO_LIMIT = 30;

const ZOOM_STEPS = [50, 65, 80, 100, 125, 150, 200];
const DEFAULT_ZOOM = 100;
const MIN_ZOOM = ZOOM_STEPS[0];
const MAX_ZOOM = ZOOM_STEPS[ZOOM_STEPS.length - 1];

const DIFF_COLOR = { 1: '#22d3ee', 2: '#a855f7', 3: '#ec4899' };

/* =====================================================================
 * Einfarbige SVG-Icons (kein Emoji, keine realistische Grafik)
 * ===================================================================== */
function IconTrash({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
            <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
        </svg>
    );
}

function IconTarget({ size = 28 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="6" />
            <circle cx="12" cy="12" r="2" />
        </svg>
    );
}

function normalizeDifficulty(raw) {
    const n = Number(raw);
    if (Number.isFinite(n)) {
        if (n <= 1) return 1;
        if (n >= 3) return 3;
        return 2;
    }
    if (typeof raw === 'string') {
        const s = raw.trim().toLowerCase();
        if (s === 'leicht' || s === 'einfach' || s === 'easy') return 1;
        if (s === 'mittel' || s === 'medium') return 2;
        if (s === 'schwer' || s === 'hard') return 3;
    }
    return 2;
}

function difficultyLabel(level) {
    if (level === 1) return 'leicht';
    if (level === 3) return 'schwer';
    return 'mittel';
}

function cellLabel(rowIdx, colIdx) {
    let s = '';
    let n = colIdx;
    do {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return `${s}${rowIdx + 1}`;
}

function trimMatrix(matrix) {
    let m = matrix.map((row) => [...row]);
    while (m.length > 1 && m[0].every((c) => c === null)) m.shift();
    while (m.length > 1 && m[m.length - 1].every((c) => c === null)) m.pop();
    if (m.length === 0) return [[null]];
    while (m[0].length > 1 && m.every((row) => row[0] === null)) m.forEach((row) => row.shift());
    while (m[0].length > 1 && m.every((row) => row[row.length - 1] === null)) m.forEach((row) => row.pop());
    if (m.every((row) => row.every((c) => c === null))) return [[null]];
    return m;
}

function matricesEqual(a, b) {
    if (!a || !b) return a === b;
    if (a.length !== b.length) return false;
    for (let r = 0; r < a.length; r++) {
        if (a[r].length !== b[r].length) return false;
        for (let c = 0; c < a[r].length; c++) {
            const x = a[r][c];
            const y = b[r][c];
            if ((x === null) !== (y === null)) return false;
            if (x && y && x.id !== y.id) return false;
        }
    }
    return true;
}

function diffCells(prev, next) {
    const changed = [];
    if (!prev || !next) return changed;
    const rows = Math.max(prev.length, next.length);
    for (let r = 0; r < rows; r++) {
        const pr = prev[r] || [];
        const nr = next[r] || [];
        const cols = Math.max(pr.length, nr.length);
        for (let c = 0; c < cols; c++) {
            const p = pr[c] || null;
            const n = nr[c] || null;
            if ((p && !n) || (!p && n) || (p && n && p.id !== n.id)) {
                changed.push(`${r}-${c}`);
            }
        }
    }
    return changed;
}

function getQuestionPlaysetIds(q) {
    if (Array.isArray(q?.playsetIds)) return q.playsetIds.map(String);
    if (Array.isArray(q?.playsets)) return q.playsets.map((p) => String(p?.id ?? p));
    return [];
}

function clampZoom(z) {
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round(z)));
}

function nextZoomStep(current, dir) {
    if (dir > 0) {
        const next = ZOOM_STEPS.find((z) => z > current);
        return next ?? MAX_ZOOM;
    }
    let prev = MIN_ZOOM;
    for (const z of ZOOM_STEPS) {
        if (z >= current) break;
        prev = z;
    }
    return prev;
}

/* =====================================================================
 * QuestionPoolItem
 * ===================================================================== */
function QuestionPoolItem({ question, usedAt, onJumpTo }) {
    const used = !!usedAt;
    const [{ isDragging }, drag] = useDrag({
        type: ItemTypes.QUESTION,
        item: { question, from: 'list' },
        canDrag: () => !used,
        collect: (m) => ({ isDragging: m.isDragging() }),
    });

    const diff = normalizeDifficulty(question.schwierigkeitsgrad);

    return (
        <div
            ref={used ? null : drag}
            className={`mxe-pool-item${used ? ' mxe-pool-item--used' : ''}`}
            style={{ opacity: isDragging ? 0.4 : undefined }}
            onClick={used && onJumpTo ? () => onJumpTo(usedAt) : undefined}
            title={used ? `Bereits in Zelle ${usedAt} – klicken zum Springen` : 'Ziehen, um in die Matrix zu legen'}
        >
            <span className={`mxe-pool-item__diff-strip mxe-pool-item__diff-strip--${diff}`} />
            <div className="mxe-pool-item__title">{question.frage}</div>
            <div className="mxe-pool-item__meta">
                <span>{question.kategorie || '—'} · {difficultyLabel(diff)}</span>
                {used && <span className="mxe-pool-item__used-badge">in {usedAt}</span>}
            </div>
        </div>
    );
}

/* =====================================================================
 * MatrixCell
 * ===================================================================== */
function MatrixCell({ rowIdx, colIdx, value, onDrop, onRemove, isConflict, registerRef }) {
    const [{ isDragging }, drag] = useDrag({
        type: ItemTypes.QUESTION,
        item: { question: value, from: 'matrix', originRow: rowIdx, originCol: colIdx },
        canDrag: () => !!value,
        collect: (m) => ({ isDragging: m.isDragging() }),
    });

    const [{ isOver }, drop] = useDrop({
        accept: ItemTypes.QUESTION,
        drop: (item) => onDrop({ kind: 'cell', rowIdx, colIdx }, item),
        collect: (m) => ({ isOver: m.isOver() }),
    });

    const setRef = useCallback((node) => {
        drag(node);
        drop(node);
        if (registerRef) registerRef(rowIdx, colIdx, node);
    }, [drag, drop, registerRef, rowIdx, colIdx]);

    if (!value) {
        return (
            <div
                ref={drop}
                className={`mxe-cell mxe-cell--empty${isOver ? ' mxe-cell--drop-over' : ''}`}
            >
                <span className="mxe-cell__coord">{cellLabel(rowIdx, colIdx)}</span>
                <span className="mxe-cell__hint">{isOver ? 'Hier ablegen' : 'leer'}</span>
            </div>
        );
    }

    const diff = normalizeDifficulty(value.schwierigkeitsgrad);
    return (
        <div
            ref={setRef}
            className={[
                'mxe-cell',
                isOver ? 'mxe-cell--drop-over' : '',
                isDragging ? 'mxe-cell--dragging' : '',
                isConflict ? 'mxe-cell--conflict' : '',
            ].join(' ').trim()}
        >
            <span className={`mxe-cell__diff-stripe mxe-cell__diff-stripe--${diff}`} />
            <span className="mxe-cell__coord">{cellLabel(rowIdx, colIdx)}</span>
            <button
                type="button"
                className="mxe-cell__remove"
                onClick={(e) => { e.stopPropagation(); onRemove(rowIdx, colIdx); }}
                title="Aus Matrix entfernen"
            >×</button>
            <div className="mxe-cell__title">{value.frage}</div>
            <div className="mxe-cell__meta">
                <span className="mxe-cell__cat">{value.kategorie || '—'}</span>
                <span className={`mxe-cell__diff-label mxe-cell__diff-label--${diff}`}>
                    {difficultyLabel(diff)}
                </span>
            </div>
        </div>
    );
}

/* =====================================================================
 * GhostZone
 * ===================================================================== */
function GhostZone({ side, rowIdx, colIdx, onDrop, dragActive }) {
    const [{ isOver }, drop] = useDrop({
        accept: ItemTypes.QUESTION,
        drop: (item) => onDrop({ kind: 'ghost', side, rowIdx, colIdx }, item),
        collect: (m) => ({ isOver: m.isOver() }),
    });
    const cls = `mxe-ghost${dragActive ? ' mxe-ghost--armed' : ''}${isOver ? ' mxe-ghost--over' : ''}`;
    return <div ref={drop} className={cls} aria-hidden="true" />;
}

/* =====================================================================
 * TrashOverlay — erscheint statt Pool, wenn eine Frage aus der Matrix
 * gezogen wird (repositionieren / entfernen)
 * ===================================================================== */
function TrashOverlay({ visible, onDrop }) {
    const [{ isOver }, drop] = useDrop({
        accept: ItemTypes.QUESTION,
        canDrop: (item) => item?.from === 'matrix',
        drop: (item) => onDrop(item),
        collect: (m) => ({ isOver: m.isOver() }),
    });
    return (
        <div
            ref={drop}
            className={[
                'mxe-trash-overlay',
                visible ? 'mxe-trash-overlay--visible' : '',
                isOver ? 'mxe-trash-overlay--over' : '',
            ].join(' ').trim()}
            aria-hidden={!visible}
        >
            <div className="mxe-trash-overlay__icon">
                <IconTrash size={56} />
            </div>
            <div className="mxe-trash-overlay__label">
                Frage hier ablegen, um sie aus der Matrix zu entfernen
            </div>
        </div>
    );
}

/* =====================================================================
 * PoolHandle — Sibling der Pool-Komponente, sichtbar wenn Pool collapsed
 * ===================================================================== */
function PoolHandle({ visible, onClick }) {
    return (
        <div
            className={`mxe-pool-handle${visible ? ' mxe-pool-handle--visible' : ''}`}
            onClick={onClick}
            role="button"
            tabIndex={visible ? 0 : -1}
            aria-label="Fragenpool anzeigen"
            aria-hidden={!visible}
        >Fragen</div>
    );
}

/* =====================================================================
 * EmptyState
 * ===================================================================== */
function EmptyState({ onDrop }) {
    const [{ isOver }, drop] = useDrop({
        accept: ItemTypes.QUESTION,
        drop: (item) => onDrop({ kind: 'init' }, item),
        collect: (m) => ({ isOver: m.isOver() }),
    });
    return (
        <div ref={drop} className={`mxe-empty-state${isOver ? ' mxe-empty-state--over' : ''}`}>
            <IconTarget size={36} />
            <div><strong>Matrix ist leer</strong></div>
            <div>Ziehe eine Frage aus dem Pool hierher, um zu beginnen.</div>
        </div>
    );
}

/* =====================================================================
 * TouchPreview — Multi-Backend-Preview, rendert nur bei Touch
 * ===================================================================== */
function TouchPreview() {
    const preview = usePreview();
    if (!preview.display) return null;
    const { item, style } = preview;
    const q = item?.question;
    if (!q) return null;
    const diff = normalizeDifficulty(q.schwierigkeitsgrad);
    return (
        <div className="mxe-touch-preview" style={style}>
            <span className={`mxe-touch-preview__stripe mxe-touch-preview__stripe--${diff}`} />
            <div className="mxe-touch-preview__title">{q.frage}</div>
            <div className="mxe-touch-preview__meta">
                {q.kategorie || '—'} · {difficultyLabel(diff)}
            </div>
        </div>
    );
}

/* =====================================================================
 * MiniMap — vollständig grid-relative Geometrie.
 *
 * Alle Maße werden mit getBoundingClientRect (fraktional, exakt) relativ
 * zum gridWrap (das tatsächlich gerenderte, transformierte Grid) gemessen.
 * Dadurch entfallen Rundungsfehler von offsetWidth/scrollWidth und das
 * Padding/Ghost-Handling, die zuvor zu akkumulierenden Abweichungen
 * (besonders rechts und beim Zoomen) geführt haben.
 *
 *   gridW, gridH   = gridWrap.getBoundingClientRect() (visuelle Grid-Größe)
 *   gx, gy         = Position der Viewport-Kante im Grid-Koordinatensystem
 *                    (= viewportRect.left - gridRect.left); negativ, wenn
 *                    links/oben noch Padding sichtbar ist.
 *   cw, ch         = sichtbarer Ausschnitt (clientWidth/Height)
 *   cellGeom       = Cell-Positionen relativ zum gridWrap (scroll-unabhängig)
 *
 * s = min(mapMaxW / gridW, mapMaxH / gridH); alle Werte * s.
 * Cells und Viewport-Rechteck teilen sich exakt dasselbe Bezugssystem.
 * ===================================================================== */
function MiniMap({ matrix, viewportRef, gridWrapRef, cellGeom }) {
    const [vp, setVp] = useState({ gridW: 0, gridH: 0, cw: 0, ch: 0, gx: 0, gy: 0, show: false });
    const rafRef = useRef(0);
    const minimapRef = useRef(null);
    const draggingRef = useRef(false);

    const recompute = useCallback(() => {
        const v = viewportRef.current;
        const g = gridWrapRef.current;
        if (!v || !g) return;
        const vRect = v.getBoundingClientRect();
        const gRect = g.getBoundingClientRect();
        const gridW = gRect.width;
        const gridH = gRect.height;
        const cw = v.clientWidth;
        const ch = v.clientHeight;
        const gx = vRect.left - gRect.left;
        const gy = vRect.top - gRect.top;
        const show = gridW > cw + 2 || gridH > ch + 2;
        setVp({ gridW, gridH, cw, ch, gx, gy, show });
    }, [viewportRef, gridWrapRef]);

    useEffect(() => {
        recompute();
        const v = viewportRef.current;
        const g = gridWrapRef.current;
        if (!v) return undefined;
        const onScroll = () => {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(recompute);
        };
        v.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', recompute);

        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recompute) : null;
        if (ro) {
            ro.observe(v);
            if (g) ro.observe(g);
        }

        return () => {
            v.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', recompute);
            if (ro) ro.disconnect();
        };
    }, [recompute, viewportRef, gridWrapRef]);

    // Bei Layout-/Zoom-Änderung (cellGeom ändert sich dabei) neu messen.
    useLayoutEffect(() => { recompute(); }, [matrix, cellGeom, recompute]);

    /* Drag-Pan in der MiniMap – grid-relative */
    useEffect(() => {
        const node = minimapRef.current;
        if (!node) return undefined;
        const mapPad = 6;
        const mapMaxW = 140;
        const mapMaxH = 90;

        const moveTo = (clientX, clientY) => {
            const v = viewportRef.current;
            const g = gridWrapRef.current;
            if (!v || !g) return;
            const rect = node.getBoundingClientRect();
            const px = clientX - rect.left - mapPad;
            const py = clientY - rect.top - mapPad;
            const gRect = g.getBoundingClientRect();
            const vRect = v.getBoundingClientRect();
            const s = Math.min(mapMaxW / Math.max(gRect.width, 1), mapMaxH / Math.max(gRect.height, 1));
            // Ziel: das Grid-Pixel unter dem Klick in die Viewport-Mitte bringen
            const gxTarget = px / s - v.clientWidth / 2;
            const gyTarget = py / s - v.clientHeight / 2;
            const gxCur = vRect.left - gRect.left;
            const gyCur = vRect.top - gRect.top;
            v.scrollLeft += gxTarget - gxCur;
            v.scrollTop += gyTarget - gyCur;
        };

        const onMouseDown = (e) => {
            e.preventDefault();
            draggingRef.current = true;
            moveTo(e.clientX, e.clientY);
        };
        const onMouseMove = (e) => {
            if (!draggingRef.current) return;
            moveTo(e.clientX, e.clientY);
        };
        const onMouseUp = () => { draggingRef.current = false; };

        const onTouchStart = (e) => {
            if (e.touches.length !== 1) return;
            e.preventDefault();
            draggingRef.current = true;
            moveTo(e.touches[0].clientX, e.touches[0].clientY);
        };
        const onTouchMove = (e) => {
            if (!draggingRef.current || e.touches.length !== 1) return;
            e.preventDefault();
            moveTo(e.touches[0].clientX, e.touches[0].clientY);
        };
        const onTouchEnd = () => { draggingRef.current = false; };

        node.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        node.addEventListener('touchstart', onTouchStart, { passive: false });
        node.addEventListener('touchmove', onTouchMove, { passive: false });
        node.addEventListener('touchend', onTouchEnd);
        return () => {
            node.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            node.removeEventListener('touchstart', onTouchStart);
            node.removeEventListener('touchmove', onTouchMove);
            node.removeEventListener('touchend', onTouchEnd);
        };
    }, [viewportRef, gridWrapRef]);

    if (!vp.show || !cellGeom) return null;

    const mapMaxW = 140;
    const mapMaxH = 90;
    const mapPad = 6;

    const s = Math.min(mapMaxW / Math.max(vp.gridW, 1), mapMaxH / Math.max(vp.gridH, 1));
    const mapW = vp.gridW * s;
    const mapH = vp.gridH * s;

    const cellWmm = cellGeom.cw * s;
    const cellHmm = cellGeom.ch * s;
    const stepXmm = (cellGeom.cw + cellGeom.gapX) * s;
    const stepYmm = (cellGeom.ch + cellGeom.gapY) * s;

    // Viewport-Rechteck im Grid-Koordinatensystem (gx/gy relativ zur Grid-Kante)
    const vpX = vp.gx * s;
    const vpY = vp.gy * s;
    const vpW = Math.max(4, vp.cw * s);
    const vpH = Math.max(4, vp.ch * s);

    return (
        <div
            ref={minimapRef}
            className="mxe-minimap"
            style={{ width: mapW + mapPad * 2, height: mapH + mapPad * 2 }}
            title="Mini-Map – klicken oder ziehen zum Navigieren"
        >
            {matrix.flatMap((row, r) =>
                row.map((cell, c) => {
                    const diff = cell ? normalizeDifficulty(cell.schwierigkeitsgrad) : null;
                    return (
                        <div
                            key={`mm-${r}-${c}`}
                            className={`mxe-minimap__cell${cell ? ' mxe-minimap__cell--filled' : ''}`}
                            style={{
                                left: mapPad + cellGeom.x0 * s + c * stepXmm,
                                top: mapPad + cellGeom.y0 * s + r * stepYmm,
                                width: Math.max(2, cellWmm),
                                height: Math.max(2, cellHmm),
                                background: cell ? DIFF_COLOR[diff] : undefined,
                            }}
                        />
                    );
                })
            )}
            <div
                className="mxe-minimap__viewport"
                style={{ left: mapPad + vpX, top: mapPad + vpY, width: vpW, height: vpH }}
            />
        </div>
    );
}

/* =====================================================================
 * QuestionPool
 * ===================================================================== */
function QuestionPool({ questions, usedMap, collapsed, onJumpTo, playsetId }) {
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');
    const [difficulty, setDifficulty] = useState('');
    const [onlyUnused, setOnlyUnused] = useState(false);

    const categories = useMemo(() => {
        const s = new Set();
        questions.forEach((q) => { if (q.kategorie) s.add(q.kategorie); });
        return Array.from(s).sort();
    }, [questions]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return questions.filter((q) => {
            if (category && q.kategorie !== category) return false;
            if (difficulty && String(normalizeDifficulty(q.schwierigkeitsgrad)) !== difficulty) return false;
            if (onlyUnused && usedMap.has(q.id)) return false;
            if (term) {
                const hay = `${q.frage || ''} ${q.kategorie || ''}`.toLowerCase();
                if (!hay.includes(term)) return false;
            }
            return true;
        });
    }, [questions, search, category, difficulty, onlyUnused, usedMap]);

    return (
        <aside className={`mxe-pool${collapsed ? ' mxe-pool--collapsed' : ''}`}>
            <div className="mxe-pool__header">
                <div className="mxe-pool__title-row">
                    <h3 className="mxe-pool__title">Fragenpool</h3>
                    <span className="mxe-pool__count">{filtered.length}/{questions.length}</span>
                </div>
                <input
                    className="mxe-pool__search"
                    type="search"
                    placeholder="Suchen…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <div className="mxe-pool__filters">
                    <select value={category} onChange={(e) => setCategory(e.target.value)}>
                        <option value="">Alle Kategorien</option>
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                        <option value="">Alle Schwierigk.</option>
                        <option value="1">leicht</option>
                        <option value="2">mittel</option>
                        <option value="3">schwer</option>
                    </select>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--muted)' }}>
                        <input
                            type="checkbox"
                            checked={onlyUnused}
                            onChange={(e) => setOnlyUnused(e.target.checked)}
                            style={{ width: 'auto', padding: 0 }}
                        />
                        nur ungenutzte
                    </label>
                </div>
            </div>
            <div className="mxe-pool__list">
                {filtered.length === 0 ? (
                    <div className="mxe-pool__empty">
                        {questions.length === 0
                            ? (playsetId
                                ? 'Dieser Show sind noch keine Fragen zugeordnet.'
                                : 'Keine Fragen gefunden.')
                            : 'Keine Treffer.'}
                    </div>
                ) : (
                    filtered.map((q) => (
                        <QuestionPoolItem
                            key={q.id}
                            question={q}
                            usedAt={usedMap.get(q.id)}
                            onJumpTo={onJumpTo}
                        />
                    ))
                )}
            </div>
        </aside>
    );
}

/* =====================================================================
 * MatrixEditorInner
 * ===================================================================== */
function MatrixEditorInner({ playsetId, matrixUrl }) {
    const [questions, setQuestions] = useState([]);
    const [questionsError, setQuestionsError] = useState('');
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    const [matrix, setMatrix] = useState([[null]]);
    const [loaded, setLoaded] = useState(false);
    const [saveStatus, setSaveStatus] = useState('saved');

    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    const [zoom, setZoom] = useState(DEFAULT_ZOOM);

    const [dragState, setDragState] = useState({ dragging: false, fromMatrix: false });
    const [gridSize, setGridSize] = useState({ w: 0, h: 0 });
    const [cellGeom, setCellGeom] = useState(null);
    const forcePoolOpenRef = useRef(false);

    const [conflictRemote, setConflictRemote] = useState(null);
    const [conflictCells, setConflictCells] = useState(new Set());

    const viewportRef = useRef(null);
    const gridWrapRef = useRef(null);
    const cellNodesRef = useRef({});
    const saveTimerRef = useRef(0);
    const inFlightSaveRef = useRef(null);
    const lastServerMatrixRef = useRef(null);
    const skipNextAutoSaveRef = useRef(false);
    const pinchStateRef = useRef(null);

    const registerCellRef = useCallback((r, c, node) => {
        if (node) cellNodesRef.current[`${r}-${c}`] = node;
    }, []);

    /* ---- Fragen laden ---- */
    const fetchQuestions = useCallback(() => {
        setLoadingQuestions(true);
        setQuestionsError('');
        axios.get(buildApiUrl('/api/questions'))
            .then((res) => setQuestions(res.data))
            .catch((err) => {
                console.error('Fehler beim Laden der Fragen:', err);
                const message = err?.response?.data || err?.message || 'Fehler beim Laden der Fragen';
                setQuestionsError(typeof message === 'string' ? message : 'Fehler beim Laden der Fragen');
            })
            .finally(() => setLoadingQuestions(false));
    }, []);
    useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

    /* ---- Matrix laden (auch bei Show-Wechsel) ---- */
    useEffect(() => {
        let cancelled = false;
        setLoaded(false);
        setUndoStack([]);
        setRedoStack([]);
        setConflictRemote(null);
        setConflictCells(new Set());
        axios.get(matrixUrl)
            .then((res) => {
                if (cancelled) return;
                const m = Array.isArray(res.data) ? res.data : [[null]];
                skipNextAutoSaveRef.current = true;
                setMatrix(m);
                lastServerMatrixRef.current = m;
            })
            .catch((err) => console.error('Fehler beim Laden der Matrix:', err))
            .finally(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, [matrixUrl, playsetId]);

    /* ---- Debounced Auto-Save ---- */
    useEffect(() => {
        if (!loaded) return undefined;
        if (skipNextAutoSaveRef.current) { skipNextAutoSaveRef.current = false; return undefined; }
        if (matricesEqual(matrix, lastServerMatrixRef.current)) return undefined;

        setSaveStatus('saving');
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            if (inFlightSaveRef.current) inFlightSaveRef.current.abort();
            const ctrl = new AbortController();
            inFlightSaveRef.current = ctrl;
            axios.post(matrixUrl, { matrix }, { signal: ctrl.signal })
                .then((res) => {
                    const saved = Array.isArray(res?.data?.matrix) ? res.data.matrix : matrix;
                    lastServerMatrixRef.current = saved;
                    setSaveStatus('saved');
                })
                .catch((err) => {
                    if (axios.isCancel(err) || err?.name === 'CanceledError') return;
                    console.error('Fehler beim Speichern der Matrix:', err);
                    setSaveStatus('error');
                });
        }, SAVE_DEBOUNCE_MS);
        return () => clearTimeout(saveTimerRef.current);
    }, [matrix, loaded, matrixUrl]);

    /* ---- Konflikt-Erkennung ---- */
    useEffect(() => {
        if (!loaded) return undefined;
        const check = () => {
            if (saveStatus === 'saving' || inFlightSaveRef.current) return;
            axios.get(matrixUrl).then((res) => {
                const remote = Array.isArray(res.data) ? res.data : null;
                if (!remote) return;
                const local = matrix;
                const baseline = lastServerMatrixRef.current;
                if (matricesEqual(local, remote)) {
                    lastServerMatrixRef.current = remote;
                    return;
                }
                if (matricesEqual(local, baseline)) {
                    const changed = diffCells(local, remote);
                    setConflictCells(new Set(changed));
                    setTimeout(() => setConflictCells(new Set()), 2200);
                    skipNextAutoSaveRef.current = true;
                    lastServerMatrixRef.current = remote;
                    setMatrix(remote);
                    return;
                }
                setConflictRemote(remote);
            }).catch(() => { /* ignore */ });
        };
        const onFocus = () => check();
        window.addEventListener('focus', onFocus);
        const id = setInterval(check, CONFLICT_POLL_MS);
        return () => { window.removeEventListener('focus', onFocus); clearInterval(id); };
    }, [matrix, matrixUrl, loaded, saveStatus]);

    const usedMap = useMemo(() => {
        const m = new Map();
        matrix.forEach((row, r) => row.forEach((cell, c) => {
            if (cell && cell.id != null) m.set(cell.id, cellLabel(r, c));
        }));
        return m;
    }, [matrix]);

    const poolQuestions = useMemo(() => {
        if (!playsetId) return questions;
        const target = String(playsetId);
        return questions.filter((q) => getQuestionPlaysetIds(q).includes(target));
    }, [questions, playsetId]);

    /* ---- Drag-Status verfolgen (ohne Verzögerung!)
       Pool slidet NUR weg, wenn aus der Matrix gezogen wird. Bei Drag aus
       dem Pool bleibt die Sidebar stehen (= kein HTML5-Drag-Source-Reflow). */
    const manager = useDragDropManager();
    useEffect(() => {
        const monitor = manager.getMonitor();
        const unsub = monitor.subscribeToStateChange(() => {
            const dragging = monitor.isDragging();
            const item = monitor.getItem();
            const fromMatrix = item?.from === 'matrix';
            if (forcePoolOpenRef.current && dragging) return;
            setDragState({ dragging, fromMatrix: dragging && fromMatrix });
        });
        return () => unsub();
    }, [manager]);

    const pushUndo = useCallback((prev) => {
        setUndoStack((u) => {
            const next = u.length >= UNDO_LIMIT ? u.slice(1) : u;
            return [...next, prev];
        });
        setRedoStack([]);
    }, []);

    const applyMatrix = useCallback((updater) => {
        setMatrix((prev) => {
            const candidate = typeof updater === 'function' ? updater(prev) : updater;
            const next = trimMatrix(candidate);
            if (matricesEqual(prev, next)) return prev;
            pushUndo(prev);
            return next;
        });
    }, [pushUndo]);

    const handleDrop = useCallback((target, item) => {
        const q = item.question;
        if (!q) return;

        applyMatrix((prev) => {
            let m = prev.map((row) => [...row]);

            if (item.from === 'matrix') {
                if (m[item.originRow] && m[item.originRow][item.originCol]
                    && m[item.originRow][item.originCol].id === q.id) {
                    m[item.originRow][item.originCol] = null;
                }
            }

            if (target.kind === 'init' || (m.length === 1 && m[0].length === 1 && m[0][0] === null)) {
                return [[q]];
            }

            const C = m[0].length;

            if (target.kind === 'cell') {
                m[target.rowIdx][target.colIdx] = q;
                return m;
            }
            if (target.kind === 'ghost') {
                const { side, rowIdx, colIdx } = target;
                if (side === 'top') {
                    const newRow = Array(C).fill(null);
                    newRow[colIdx] = q;
                    return [newRow, ...m];
                }
                if (side === 'bottom') {
                    const newRow = Array(C).fill(null);
                    newRow[colIdx] = q;
                    return [...m, newRow];
                }
                if (side === 'left') {
                    return m.map((row, idx) => {
                        const nr = [null, ...row];
                        if (idx === rowIdx) nr[0] = q;
                        return nr;
                    });
                }
                if (side === 'right') {
                    return m.map((row, idx) => {
                        const nr = [...row, null];
                        if (idx === rowIdx) nr[nr.length - 1] = q;
                        return nr;
                    });
                }
            }
            return m;
        });
    }, [applyMatrix]);

    const handleRemoveCell = useCallback((rowIdx, colIdx) => {
        applyMatrix((prev) => {
            const m = prev.map((row) => [...row]);
            m[rowIdx][colIdx] = null;
            return m;
        });
    }, [applyMatrix]);

    const handleTrashDrop = useCallback((item) => {
        if (item?.from !== 'matrix') return;
        applyMatrix((prev) => {
            const m = prev.map((row) => [...row]);
            if (m[item.originRow] && m[item.originRow][item.originCol]) {
                m[item.originRow][item.originCol] = null;
            }
            return m;
        });
    }, [applyMatrix]);

    const addRow = useCallback((where) => {
        applyMatrix((prev) => {
            const C = prev[0]?.length || 1;
            if (prev.length === 1 && prev[0].every((c) => c === null)) return prev;
            const newRow = Array(C).fill(null);
            return where === 'top' ? [newRow, ...prev] : [...prev, newRow];
        });
    }, [applyMatrix]);

    const addCol = useCallback((where) => {
        applyMatrix((prev) => {
            if (prev.length === 1 && prev[0].every((c) => c === null)) return prev;
            return prev.map((row) => where === 'left' ? [null, ...row] : [...row, null]);
        });
    }, [applyMatrix]);

    const handleClear = useCallback(() => {
        if (matrix.length === 1 && matrix[0][0] === null) return;
        if (!window.confirm('Matrix komplett leeren? Diese Aktion kann mit Strg+Z rückgängig gemacht werden.')) return;
        applyMatrix(() => [[null]]);
    }, [matrix, applyMatrix]);

    const undo = useCallback(() => {
        setUndoStack((u) => {
            if (!u.length) return u;
            const prev = u[u.length - 1];
            setRedoStack((r) => [...r, matrix]);
            setMatrix(prev);
            return u.slice(0, -1);
        });
    }, [matrix]);

    const redo = useCallback(() => {
        setRedoStack((r) => {
            if (!r.length) return r;
            const next = r[r.length - 1];
            setUndoStack((u) => [...u, matrix]);
            setMatrix(next);
            return r.slice(0, -1);
        });
    }, [matrix]);

    useEffect(() => {
        const onKey = (e) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            const tag = (e.target?.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
            if (e.key === 'z' || e.key === 'Z') {
                if (e.shiftKey) { e.preventDefault(); redo(); }
                else { e.preventDefault(); undo(); }
            } else if (e.key === 'y' || e.key === 'Y') {
                e.preventDefault(); redo();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [undo, redo]);

    /* ---- Wheel-Zoom (Strg + Mausrad / Trackpad-Pinch) ---- */
    useEffect(() => {
        const v = viewportRef.current;
        if (!v) return undefined;

        const onWheel = (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const dir = e.deltaY > 0 ? -1 : 1;
                setZoom((z) => clampZoom(z + dir * 5));
            }
        };

        const dist = (t1, t2) => {
            const dx = t1.clientX - t2.clientX;
            const dy = t1.clientY - t2.clientY;
            return Math.sqrt(dx * dx + dy * dy);
        };

        const onTouchStart = (e) => {
            if (e.touches.length === 2) {
                pinchStateRef.current = {
                    initDist: dist(e.touches[0], e.touches[1]),
                    initZoom: zoom,
                };
            } else {
                pinchStateRef.current = null;
            }
        };
        const onTouchMove = (e) => {
            const st = pinchStateRef.current;
            if (st && e.touches.length === 2) {
                const d = dist(e.touches[0], e.touches[1]);
                const ratio = d / st.initDist;
                setZoom(clampZoom(Math.round(st.initZoom * ratio)));
                e.preventDefault();
            }
        };
        const onTouchEnd = () => { pinchStateRef.current = null; };

        v.addEventListener('wheel', onWheel, { passive: false });
        v.addEventListener('touchstart', onTouchStart, { passive: false });
        v.addEventListener('touchmove', onTouchMove, { passive: false });
        v.addEventListener('touchend', onTouchEnd);
        v.addEventListener('touchcancel', onTouchEnd);
        return () => {
            v.removeEventListener('wheel', onWheel);
            v.removeEventListener('touchstart', onTouchStart);
            v.removeEventListener('touchmove', onTouchMove);
            v.removeEventListener('touchend', onTouchEnd);
            v.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [zoom]);

    /* ---- Pan im Viewport (Google-Maps-Style) für Maus und Touch
       Pan startet auf:
       - dem Viewport-Hintergrund (padding-Bereich)
       - leeren Matrix-Zellen ohne Frage
       - Ghost-Zonen (sie sind Drop-Targets, aber kein Drag-Source)
       Pan startet NICHT auf:
       - belegten Matrix-Zellen (D&D Source)
       - Pool-Items (D&D Source)
       - Buttons / Form-Elemente
       Außerdem: falls bereits ein Drag läuft, übernimmt das D&D-System. */
    useEffect(() => {
        const v = viewportRef.current;
        if (!v) return undefined;
        let panning = null;

        const isInteractive = (target) => {
            if (!target || !target.closest) return false;
            // Belegte (drag-fähige) Zelle? mxe-cell--empty NICHT als interaktiv
            // werten, sondern als pan-Fläche.
            const filledCell = target.closest('.mxe-cell:not(.mxe-cell--empty)');
            if (filledCell) return true;
            return !!target.closest('.mxe-pool-item, .mxe-pool-handle, button, input, select, a');
        };

        const isDragActive = () => manager.getMonitor().isDragging();

        const startPan = (clientX, clientY) => {
            panning = { startX: clientX, startY: clientY, sl: v.scrollLeft, st: v.scrollTop };
            v.classList.add('mxe-viewport--panning');
        };
        const movePan = (clientX, clientY) => {
            if (!panning) return;
            v.scrollLeft = panning.sl - (clientX - panning.startX);
            v.scrollTop = panning.st - (clientY - panning.startY);
        };
        const endPan = () => {
            panning = null;
            v.classList.remove('mxe-viewport--panning');
        };

        const onMouseDown = (e) => {
            if (e.button !== 0) return;
            if (isDragActive()) return;
            if (isInteractive(e.target)) return;
            startPan(e.clientX, e.clientY);
            e.preventDefault();
        };
        const onMouseMove = (e) => movePan(e.clientX, e.clientY);
        const onMouseUp = () => endPan();

        const onTouchStart = (e) => {
            if (e.touches.length !== 1) { endPan(); return; }
            if (isDragActive()) return;
            if (isInteractive(e.target)) return;
            const t = e.touches[0];
            startPan(t.clientX, t.clientY);
        };
        const onTouchMove = (e) => {
            if (!panning || e.touches.length !== 1) return;
            const t = e.touches[0];
            movePan(t.clientX, t.clientY);
            e.preventDefault();
        };
        const onTouchEnd = () => endPan();

        v.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        v.addEventListener('touchstart', onTouchStart, { passive: true });
        v.addEventListener('touchmove', onTouchMove, { passive: false });
        v.addEventListener('touchend', onTouchEnd);
        v.addEventListener('touchcancel', onTouchEnd);
        return () => {
            v.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            v.removeEventListener('touchstart', onTouchStart);
            v.removeEventListener('touchmove', onTouchMove);
            v.removeEventListener('touchend', onTouchEnd);
            v.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [manager]);

    const acceptRemote = () => {
        if (!conflictRemote) return;
        const changed = diffCells(matrix, conflictRemote);
        setConflictCells(new Set(changed));
        setTimeout(() => setConflictCells(new Set()), 2200);
        skipNextAutoSaveRef.current = true;
        lastServerMatrixRef.current = conflictRemote;
        setMatrix(conflictRemote);
        setConflictRemote(null);
    };
    const keepMine = () => {
        setConflictRemote(null);
        lastServerMatrixRef.current = null;
        setMatrix((m) => m.map((row) => [...row]));
    };

    const jumpToCell = useCallback((label) => {
        for (let r = 0; r < matrix.length; r++) {
            for (let c = 0; c < matrix[r].length; c++) {
                if (cellLabel(r, c) === label) {
                    const node = cellNodesRef.current[`${r}-${c}`];
                    if (node && viewportRef.current) {
                        const v = viewportRef.current;
                        const nrect = node.getBoundingClientRect();
                        const vrect = v.getBoundingClientRect();
                        v.scrollLeft += nrect.left - vrect.left - vrect.width / 2 + nrect.width / 2;
                        v.scrollTop += nrect.top - vrect.top - vrect.height / 2 + nrect.height / 2;
                        if (node.animate) {
                            node.animate(
                                [{ outline: '2px solid rgba(34,211,238,0.9)' }, { outline: '2px solid transparent' }],
                                { duration: 1400 }
                            );
                        }
                    }
                    return;
                }
            }
        }
    }, [matrix]);

    const isEmpty = matrix.length === 1 && matrix[0].length === 1 && matrix[0][0] === null;
    const R = matrix.length;
    const C = matrix[0]?.length || 1;

    const dragging = dragState.dragging;
    const poolCollapsed = dragState.dragging && dragState.fromMatrix;
    const trashVisible = dragState.dragging && dragState.fromMatrix;

    const gridStyle = isEmpty
        ? { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }
        : {
            gridTemplateColumns: `auto repeat(${C}, auto) auto`,
            gridAutoRows: 'auto',
        };

    const renderCells = () => {
        if (isEmpty) return <EmptyState onDrop={handleDrop} />;
        const cells = [];

        cells.push(<div key="tl" />);
        for (let c = 0; c < C; c++) {
            cells.push(
                <GhostZone key={`tg-${c}`} side="top" rowIdx={-1} colIdx={c} onDrop={handleDrop} dragActive={dragging} />
            );
        }
        cells.push(<div key="tr" />);

        for (let r = 0; r < R; r++) {
            cells.push(
                <GhostZone key={`lg-${r}`} side="left" rowIdx={r} colIdx={-1} onDrop={handleDrop} dragActive={dragging} />
            );
            for (let c = 0; c < C; c++) {
                cells.push(
                    <MatrixCell
                        key={`cell-${r}-${c}`}
                        rowIdx={r}
                        colIdx={c}
                        value={matrix[r][c]}
                        onDrop={handleDrop}
                        onRemove={handleRemoveCell}
                        isConflict={conflictCells.has(`${r}-${c}`)}
                        registerRef={registerCellRef}
                    />
                );
            }
            cells.push(
                <GhostZone key={`rg-${r}`} side="right" rowIdx={r} colIdx={C} onDrop={handleDrop} dragActive={dragging} />
            );
        }

        cells.push(<div key="bl" />);
        for (let c = 0; c < C; c++) {
            cells.push(
                <GhostZone key={`bg-${c}`} side="bottom" rowIdx={R} colIdx={c} onDrop={handleDrop} dragActive={dragging} />
            );
        }
        cells.push(<div key="br" />);

        return cells;
    };

    const totalCells = R * C;
    const filledCells = matrix.reduce((sum, row) => sum + row.filter(Boolean).length, 0);

    const uncollapse = () => {
        forcePoolOpenRef.current = true;
        setDragState({ dragging: false, fromMatrix: false });
        setTimeout(() => { forcePoolOpenRef.current = false; }, 400);
    };

    /* Sizer für transform-zoom: damit der Scroll-Container exakt den
       visuellen Platz des skalierten Grids reserviert, messen wir die
       natural-size des grid-wrap fraktional (getBoundingClientRect / scale,
       nicht das gerundete offsetWidth). */
    useLayoutEffect(() => {
        const node = gridWrapRef.current;
        if (!node) return;
        const scale = zoom / 100 || 1;
        const rect = node.getBoundingClientRect();
        const w = rect.width / scale;
        const h = rect.height / scale;
        if (Math.abs(w - gridSize.w) > 0.5 || Math.abs(h - gridSize.h) > 0.5) {
            setGridSize({ w, h });
        }
    }, [matrix, zoom, gridSize.w, gridSize.h]);

    /* Cell-Geometrie für die MiniMap aus dem DOM messen – relativ zum
       gridWrap (transformiertes Grid). Diese Werte sind scroll-unabhängig
       und werden mit getBoundingClientRect (fraktional, exakt) gemessen,
       damit es keine Rundungsfehler wie bei offsetWidth/scrollWidth gibt. */
    useLayoutEffect(() => {
        const g = gridWrapRef.current;
        const c00 = cellNodesRef.current['0-0'];
        if (!g || !c00) {
            setCellGeom(null);
            return;
        }
        const gRect = g.getBoundingClientRect();
        const r00 = c00.getBoundingClientRect();
        // Position der ersten Zelle relativ zur linken/oberen Kante des Grids
        const x0 = r00.left - gRect.left;
        const y0 = r00.top - gRect.top;
        const cw = r00.width;
        const ch = r00.height;

        let gapX = 0;
        const c01 = cellNodesRef.current['0-1'];
        if (c01) {
            const r01 = c01.getBoundingClientRect();
            gapX = r01.left - r00.right;
        }
        let gapY = 0;
        const c10 = cellNodesRef.current['1-0'];
        if (c10) {
            const r10 = c10.getBoundingClientRect();
            gapY = r10.top - r00.bottom;
        }

        setCellGeom({ x0, y0, cw, ch, gapX, gapY });
    }, [matrix, zoom, gridSize.w, gridSize.h]);

    const zoomIn = () => setZoom((z) => nextZoomStep(z, 1));
    const zoomOut = () => setZoom((z) => nextZoomStep(z, -1));

    return (
        <div className={`mxe-root${dragging ? ' mxe-root--dragging' : ''}`}>
            <TouchPreview />

            <div>
                <div className="mxe-toolbar">
                    <div className="mxe-toolbar__group">
                        <button type="button" className="mxe-tool-btn" onClick={undo} disabled={!undoStack.length} title="Rückgängig (Strg+Z)">↶</button>
                        <button type="button" className="mxe-tool-btn" onClick={redo} disabled={!redoStack.length} title="Wiederherstellen (Strg+Shift+Z)">↷</button>
                    </div>

                    <div className="mxe-toolbar__group">
                        <button type="button" className="mxe-tool-btn" onClick={() => addRow('top')} title="Zeile oben" disabled={isEmpty}>＋ Zeile↑</button>
                        <button type="button" className="mxe-tool-btn" onClick={() => addRow('bottom')} title="Zeile unten" disabled={isEmpty}>＋ Zeile↓</button>
                        <button type="button" className="mxe-tool-btn" onClick={() => addCol('left')} title="Spalte links" disabled={isEmpty}>＋ Spalte←</button>
                        <button type="button" className="mxe-tool-btn" onClick={() => addCol('right')} title="Spalte rechts" disabled={isEmpty}>＋ Spalte→</button>
                    </div>

                    <button type="button" className="mxe-tool-btn mxe-tool-btn--danger" onClick={handleClear} title="Matrix leeren" disabled={isEmpty}><IconTrash /> <span>Leeren</span></button>

                    <div className="mxe-toolbar__spacer" />

                    <span className="mxe-status-pill" title="Speicherstatus / Größe / Belegung">
                        <span className={
                            saveStatus === 'saving' ? 'mxe-status-dot mxe-status-dot--saving'
                                : saveStatus === 'error' ? 'mxe-status-dot mxe-status-dot--error'
                                : 'mxe-status-dot'
                        } />
                        {saveStatus === 'saving' ? 'Speichert…' : saveStatus === 'error' ? 'Fehler' : 'Gespeichert'}
                        {' · '}{R}×{C} · {filledCells}/{totalCells}
                    </span>
                </div>

                {conflictRemote && (
                    <div className="mxe-conflict">
                        <span className="mxe-conflict__msg">
                            ⚠ Diese Matrix wurde an anderer Stelle geändert. Deine lokalen Änderungen sind noch nicht gespeichert.
                        </span>
                        <button type="button" className="mxe-tool-btn" onClick={acceptRemote}>Neu laden</button>
                        <button type="button" className="mxe-tool-btn" onClick={keepMine}>Meine Version behalten</button>
                    </div>
                )}

                {questionsError && (
                    <div className="mxe-conflict" style={{ background: 'rgba(239,68,68,0.12)', borderColor: 'rgba(239,68,68,0.45)', color: '#fecaca' }}>
                        <span className="mxe-conflict__msg">{questionsError}</span>
                        <button type="button" className="mxe-tool-btn" onClick={fetchQuestions}>Erneut versuchen</button>
                    </div>
                )}
            </div>

            <div className="mxe-main">
                <div className="mxe-workspace">
                    <div className="mxe-viewport" ref={viewportRef}>
                        <div
                            className="mxe-sizer"
                            style={gridSize.w ? {
                                width: gridSize.w * zoom / 100,
                                height: gridSize.h * zoom / 100,
                            } : undefined}
                        >
                            <div
                                className="mxe-grid-wrap"
                                ref={gridWrapRef}
                                style={{ transform: `scale(${zoom / 100})` }}
                            >
                                <div className="mxe-grid" style={gridStyle}>
                                    {renderCells()}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mxe-zoom-controls">
                        <button type="button" className="mxe-zoom-btn" onClick={zoomOut} disabled={zoom <= MIN_ZOOM} title="Verkleinern">−</button>
                        <span className="mxe-zoom-label">{zoom}%</span>
                        <button type="button" className="mxe-zoom-btn" onClick={zoomIn} disabled={zoom >= MAX_ZOOM} title="Vergrößern">+</button>
                    </div>

                    <MiniMap matrix={matrix} viewportRef={viewportRef} gridWrapRef={gridWrapRef} cellGeom={cellGeom} />
                </div>

                <div className="mxe-side-area">
                    <QuestionPool
                        questions={poolQuestions}
                        usedMap={usedMap}
                        collapsed={poolCollapsed}
                        onJumpTo={jumpToCell}
                        playsetId={playsetId}
                    />
                    <TrashOverlay visible={trashVisible} onDrop={handleTrashDrop} />
                    <PoolHandle visible={poolCollapsed} onClick={uncollapse} />
                </div>
            </div>

            {loadingQuestions && (
                <div style={{ position: 'absolute', top: 70, right: 12, color: 'var(--muted)', fontSize: 12 }}>
                    Fragen werden geladen…
                </div>
            )}
        </div>
    );
}

/* =====================================================================
 * Public Component
 * ===================================================================== */
const MatrixEditor = ({ playsetId: playsetIdProp } = {}) => {
    const params = useParams();
    const explicitPlaysetId = playsetIdProp ?? params?.id ?? null;

    const [activePlaysetId, setActivePlaysetId] = useState(null);
    const [shows, setShows] = useState([]);
    const [activeShowName, setActiveShowName] = useState('');

    useEffect(() => {
        if (explicitPlaysetId) return undefined;
        let cancelled = false;
        Promise.all([loadActiveShow(), loadPlaysets()])
            .then(([active, allShows]) => {
                if (cancelled) return;
                const safeShows = Array.isArray(allShows) ? allShows : [];
                setShows(safeShows);
                const activeId = active?.playsetId ?? null;
                setActivePlaysetId(activeId);
                const found = safeShows.find((s) => String(s.id) === String(activeId));
                setActiveShowName(found?.name || '');
            })
            .catch(() => { /* ignore */ });
        return () => { cancelled = true; };
    }, [explicitPlaysetId]);

    const onShowActiveChange = useCallback((payload) => {
        const newId = payload?.playsetId ?? null;
        setActivePlaysetId(newId);
        const found = shows.find((s) => String(s.id) === String(newId));
        setActiveShowName(found?.name || '');
    }, [shows]);
    useSocketEvent(socket, 'show:active', onShowActiveChange);

    const playsetId = explicitPlaysetId ?? activePlaysetId;
    const matrixUrl = explicitPlaysetId
        ? buildApiUrl(`/api/playsets/${explicitPlaysetId}/matrix`)
        : buildApiUrl('/api/matrix');

    return (
        <DndProvider options={HTML5toTouch}>
            {!explicitPlaysetId && activeShowName && (
                <div className="mxe-conflict" style={{
                    background: 'rgba(34, 211, 238, 0.08)',
                    borderColor: 'rgba(34, 211, 238, 0.35)',
                    color: 'var(--text)',
                    marginBottom: 12,
                }}>
                    Aktive Show: <strong style={{ marginLeft: 4 }}>{activeShowName}</strong>
                </div>
            )}
            <MatrixEditorInner
                key={`${matrixUrl}::${playsetId || 'none'}`}
                playsetId={playsetId}
                matrixUrl={matrixUrl}
            />
        </DndProvider>
    );
};

export default MatrixEditor;
